// The setup screen's draft: what it shows, how an edit reaches the server.
//
// Tenant mode: the EFFECTIVE draft (design layer + this organization's draft,
// every entry stamped with its origin) is edited in the browser and saved as
// the tenant BODY (model.tenantBodyOf) through `patch-draft`, debounced, with
// the draft revision the last answer carried as `base_version`. Every edit is
// kept as an operation addressed by KEYS (form no, set key, column name), so
// when someone else saved first (409) the screen reloads the server's draft
// and replays the edits not yet saved on top of it, then saves again. A
// refused edit (422) keeps the edit on screen with the policy's reasons; the
// person fixes it (the next save carries the fix) or discards what is unsaved.
//
// Design mode: the layer is the caller's (`transaction.uda` in the
// transaction designer); every edit is handed back through onChange and the
// designer's own Save writes it. No server draft, no publish.

import { useCallback, useEffect, useRef, useState } from 'react'
import type { UdaColumnType, UdaErrorDetail, UdaGetResult, UdaLayer, UdaTransport } from '../types'
import { clone, emptyLayer, publishedIndex, tenantBodyOf, type SetupMode } from './model'

export const UDA_SAVE_DEBOUNCE_MS = 600
/** How many times a stale-version save reloads and replays before giving up. */
const MAX_REPLAYS = 3

export type LayerOp = (layer: UdaLayer) => void

export interface UdaDraftError {
  kind: 'refused' | 'conflict' | 'failed' | 'load'
  message: string
  errors?: string[]
}

export interface UdaDraftState {
  mode: SetupMode
  loading: boolean
  /** The layer the screen edits (effective in tenant mode). */
  layer: UdaLayer
  /** The design layer (tenant mode: from the model; design mode: the layer). */
  design: UdaLayer
  /** Draft revision every write quotes. */
  version: number
  publishedVersion: number
  /** What publishing would change (plain sentences, from the server). */
  changes: string[]
  /** Warnings the last save returned (a value the master lacks, …). */
  problems: string[]
  columnTypes: UdaColumnType[]
  /** Columns that are published (values may exist), with their codes. */
  published: ReturnType<typeof publishedIndex>
  saving: boolean
  /** Edits made but not saved yet. */
  pending: number
  error: UdaDraftError | null
  /** A notice after a reload-and-replay. */
  notice: string | null
}

export interface UdaDraftApi extends UdaDraftState {
  edit(op: LayerOp): void
  /** Save what is pending now; resolves true when nothing is left unsaved. */
  flush(): Promise<boolean>
  reload(): Promise<void>
  /** Throw away unsaved edits and show the server's draft again. */
  discard(): Promise<void>
  clearNotice(): void
}

function detailOf(err: unknown): { status: number | null; detail: UdaErrorDetail | null; message: string } {
  const e = err as { status?: number; response?: { status?: number; data?: { detail?: unknown } }; detail?: unknown; message?: string }
  const status = typeof e?.status === 'number' ? e.status : (typeof e?.response?.status === 'number' ? e.response.status : null)
  const raw = e?.detail ?? e?.response?.data?.detail
  const detail = raw && typeof raw === 'object' ? raw as UdaErrorDetail : null
  const message = detail?.message || (typeof raw === 'string' ? raw : '') || e?.message || 'The request failed'
  return { status, detail, message }
}

function mergedPublished(get: UdaGetResult | null): ReturnType<typeof publishedIndex> {
  const out = publishedIndex(get?.design)
  for (const [k, v] of publishedIndex(get?.published)) {
    const had = out.get(k)
    out.set(k, had ? { col: { ...had.col, ...v.col }, codes: [...new Set([...had.codes, ...v.codes])] } : v)
  }
  return out
}

export function useUdaDraft(opts: {
  mode: SetupMode
  transport: UdaTransport
  objName: string
  /** Design mode only. */
  layer?: UdaLayer
  onChange?: (layer: UdaLayer) => void
  columnTypes?: UdaColumnType[]
}): UdaDraftApi {
  const { mode, transport, objName } = opts
  const design = mode === 'design'
  const [state, setState] = useState<UdaDraftState>(() => ({
    mode, loading: !design, layer: design ? clone(opts.layer || emptyLayer()) : emptyLayer(),
    design: design ? clone(opts.layer || emptyLayer()) : emptyLayer(), version: 0, publishedVersion: 0,
    changes: [], problems: [], columnTypes: opts.columnTypes || [], published: new Map(), saving: false,
    pending: 0, error: null, notice: null,
  }))
  const stateRef = useRef(state)
  stateRef.current = state
  const ops = useRef<LayerOp[]>([])
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inflight = useRef<Promise<boolean> | null>(null)
  const alive = useRef(true)
  useEffect(() => { alive.current = true; return () => { alive.current = false } }, [])
  const onChangeRef = useRef(opts.onChange)
  onChangeRef.current = opts.onChange

  // Design mode follows the caller's layer when it changes from outside.
  useEffect(() => {
    if (!design || !opts.layer) return
    setState(s => (s.layer === opts.layer ? s : { ...s, layer: clone(opts.layer!), design: clone(opts.layer!) }))
  }, [design, opts.layer])

  // Also updates the ref at once, so a save loop that continues after an
  // await reads what it just set, not the last render's state.
  const set = useCallback((patch: Partial<UdaDraftState>) => {
    stateRef.current = { ...stateRef.current, ...patch }
    if (alive.current) setState(s => ({ ...s, ...patch }))
  }, [])

  const fromGet = useCallback((g: UdaGetResult): Partial<UdaDraftState> => ({
    design: g.design || emptyLayer(), version: g.version || 0, publishedVersion: g.published_version || 0,
    changes: g.changes || [], columnTypes: g.column_types || stateRef.current.columnTypes,
    published: mergedPublished(g),
  }), [])

  const load = useCallback(async (): Promise<UdaGetResult | null> => {
    try {
      const g = await transport.get(objName)
      return g
    } catch (err) {
      const d = detailOf(err)
      console.error('[uda setup] loading the attribute settings failed', err)
      set({ loading: false, error: { kind: 'load', message: d.message } })
      return null
    }
  }, [transport, objName, set])

  const reload = useCallback(async () => {
    if (design) return
    set({ loading: true })
    const g = await load()
    if (!g) return
    ops.current = []
    set({ ...fromGet(g), layer: clone(g.effective_draft || emptyLayer()), loading: false, error: null, pending: 0 })
  }, [design, load, fromGet, set])

  useEffect(() => { if (!design) void reload() }, [design, reload])

  const refreshChanges = useCallback(async () => {
    try {
      const g = await transport.get(objName)
      set({ changes: g.changes || [], publishedVersion: g.published_version || 0, published: mergedPublished(g) })
    } catch (err) {
      console.warn('[uda setup] the change list could not be refreshed', err)
    }
  }, [transport, objName, set])

  const saveNow = useCallback(async (): Promise<boolean> => {
    if (design) return true
    if (inflight.current) await inflight.current
    if (!ops.current.length) return true
    const run = (async (): Promise<boolean> => {
      let replays = 0
      for (;;) {
        const s = stateRef.current
        const count = ops.current.length
        if (!count) return true
        set({ saving: true })
        try {
          const res = await transport.patchDraft(objName, tenantBodyOf(s.layer, s.design), s.version)
          ops.current = ops.current.slice(count)
          set({ version: res.version, problems: res.problems || [], saving: false, error: null, pending: ops.current.length })
          void refreshChanges()
          if (!ops.current.length) return true
          continue
        } catch (err) {
          const d = detailOf(err)
          if (d.status === 409 && replays < MAX_REPLAYS) {
            replays++
            console.info('[uda setup] the draft changed on the server — reloading and replaying', ops.current.length, 'edit(s)')
            const g = await load()
            if (!g) { set({ saving: false }); return false }
            const base = clone(g.effective_draft || emptyLayer())
            try {
              for (const op of ops.current) op(base)
            } catch (replayErr) {
              console.error('[uda setup] an edit could not be replayed on the newer draft', replayErr)
              ops.current = []
              set({ ...fromGet(g), layer: base, saving: false, pending: 0,
                error: { kind: 'conflict', message: 'Someone else changed these settings at the same time, and your last change no longer fits. The newer settings are shown; make the change again.' } })
              return false
            }
            set({ ...fromGet(g), layer: base, notice: 'Someone else saved these settings at the same time. Their changes are kept and yours were applied on top.' })
            continue
          }
          if (d.status === 422) {
            set({ saving: false, error: { kind: 'refused', message: d.message || 'This change is not allowed.', errors: d.detail?.details?.errors || [] } })
          } else if (d.status === 409) {
            set({ saving: false, error: { kind: 'conflict', message: 'These settings keep changing elsewhere. Reload to see the latest.' } })
          } else {
            console.error('[uda setup] saving the draft failed', err)
            set({ saving: false, error: { kind: 'failed', message: `Your change was not saved: ${d.message}` } })
          }
          return false
        }
      }
    })()
    inflight.current = run
    try { return await run } finally { inflight.current = null }
  }, [design, transport, objName, set, load, fromGet, refreshChanges])

  const edit = useCallback((op: LayerOp) => {
    const s = stateRef.current
    const next = clone(s.layer)
    try {
      op(next)
    } catch (err) {
      console.error('[uda setup] an edit failed', err)
      set({ error: { kind: 'failed', message: `That change could not be made: ${(err as Error)?.message || err}` } })
      return
    }
    stateRef.current = { ...s, layer: next }
    if (design) {
      set({ layer: next, design: next })
      onChangeRef.current?.(next)
      return
    }
    ops.current.push(op)
    set({ layer: next, pending: ops.current.length })
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => { timer.current = null; void saveNow() }, UDA_SAVE_DEBOUNCE_MS)
  }, [design, set, saveNow])

  const flush = useCallback(async () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null }
    return saveNow()
  }, [saveNow])

  const discard = useCallback(async () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null }
    ops.current = []
    await reload()
  }, [reload])

  // Leaving with unsaved edits: send them (best effort).
  useEffect(() => () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; void saveNow() }
  }, [saveNow])

  return {
    ...state, edit, flush, reload, discard,
    clearNotice: () => set({ notice: null }),
  }
}
